import React from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import EllipsisHover from '@/pages/home/components/EllipsisHover';
import { getCalcMetricTranslateKey } from '@/pages/intelligence/utils/calcMetrics';
import { addFormatMessageSafelyHandler } from '../../../../utils';

import './index.scss';

import type { IBaseProps, IMetricCalcMode } from '@/pages/intelligence/types/common';
import type { IEntityCumulativeMetricDataGetter } from './types';

const METRICS: {
  [key: string]: string;
} = {
  'steamcharts###pcu': 'pcu',
  'steamcharts###acu': 'acu',
  'newzoo###mau': 'mau',
  'mscience###revenue': 'digital_revenue',
  'mscience###units': 'units',
  'mscience###ast': 'ast',
  'mscience###arppu': 'digital_arppu',
  'npd###revenue': 'physical_revenue',
};

interface IEntitiesMetricsCumulativeCompareTableProps extends IBaseProps {
  metrics: string[];
  metricsNamesMapping: {
    [key: string]: string;
  };
  metricsCalcModesMapping: {
    [key: string]: IMetricCalcMode;
  };
  metricsCalcModesNamesMapping: {
    [key in IMetricCalcMode]: string;
  };
  metricColumnRenderer?: (metric: string, rowIndex: number) => React.ReactElement;
  entities: any[];
  getEntityCumulativeMetricData: IEntityCumulativeMetricDataGetter;
  defaultMetricValueFormatter?: (value: any) => any;
  metricValueFormattersMapping?: {
    [key: string]: (value: any) => any;
  };
  placeholder?: string;
}

export default function EntitiesMetricsCumulativeCompareTable(props: IEntitiesMetricsCumulativeCompareTableProps) {
  const {
    metrics,
    // metricsNamesMapping,
    metricsCalcModesMapping,
    // metricsCalcModesNamesMapping,
    metricColumnRenderer: metricColumnRendererOutter,
    entities,
    getEntityCumulativeMetricData,
    defaultMetricValueFormatter,
    metricValueFormattersMapping,
    placeholder = '-',
    style,
    className,
  } = props;

  const { formatMessage } = useIntl();
  const formatMessageSafely = addFormatMessageSafelyHandler(formatMessage);

  const defaultMetricColumnRender = (metric: string): React.ReactElement => {
    const metricCalcMode = metricsCalcModesMapping?.[metric];
    const translateKey = getCalcMetricTranslateKey(METRICS[metric], metricCalcMode);
    const calcMetricText = formatMessageSafely({ id: translateKey });
    return <EllipsisHover text={calcMetricText} />;
  };

  const metricColumnRenderer =
    typeof metricColumnRendererOutter === 'function' ? metricColumnRendererOutter : defaultMetricColumnRender;

  return (
    <div style={style} className={classnames('dbi-entities-metrics-cumulative-compare-table', className)}>
      <div className="dbi-cumulative_table-body">
        {metrics?.map((metric, rowIndex) => {
          const metricValueFormatter = metricValueFormattersMapping?.[metric] || defaultMetricValueFormatter;
          const hasMetricValueFormatter = typeof metricValueFormatter === 'function';
          return (
            <div key={metric} className="dbi-cumulative_table-row">
              <div className="dbi-cumulative_table-column first-column">
                <div className="dbi-cumulative_table-column-content">
                  {metricColumnRenderer(metric, rowIndex) || placeholder}
                </div>
              </div>
              {entities?.map((entity, entityIndex) => {
                const metricData = getEntityCumulativeMetricData(entity, metric);
                const metricValue = metricData?.value;
                const formattedMetricValue = hasMetricValueFormatter ? metricValueFormatter(metricValue) : metricValue;
                return (
                  <div key={entityIndex} className="dbi-cumulative_table-column">
                    <div className="dbi-cumulative_table-column-content">
                      {formattedMetricValue}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
